import { useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, LineChart, Line,
} from "recharts";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const TOOLTIP_STYLE = {
  background: "#031c18", border: "1px solid #1a4640",
  color: "#fdfbf7", fontSize: 12, borderRadius: 2,
};

function StatCard({ label, value, accent = "#d4af37", testId, hint }) {
  return (
    <div className="lux-card p-5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</div>
      <div className="font-display text-3xl mt-1.5" style={{ color: accent }}>{value}</div>
      {hint && <div className="text-[10px] text-[#a0b2af] mt-1">{hint}</div>}
    </div>
  );
}

/** /admin/nps — Net Promoter Score dashboard.
 *  Aggregates 1-click votes captured from the post-cleaning email.
 *  Key metric is NPS = %promoters − %detractors (range −100 … +100).
 */
export default function AdminNps() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, r] = await Promise.all([
          api.get("/admin/nps-stats?months=6"),
          api.get("/admin/nps-recent?limit=20"),
        ]);
        if (!cancelled) {
          setStats(s.data);
          setRecent(r.data.items || []);
        }
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="text-[#a0b2af]">A carregar NPS…</div>;

  const totals = stats?.totals || {};
  const series = stats?.series || [];
  const npsColor = totals.nps >= 50 ? "#10b981" : totals.nps >= 0 ? "#f59e0b" : "#ef4444";
  const hasData = series.some(s => s.n > 0);

  return (
    <div className="space-y-8" data-testid="admin-nps">
      <div>
        <div className="eyebrow mb-3">Satisfação · Net Promoter Score</div>
        <h1 className="font-display text-5xl">NPS.</h1>
        <p className="text-sm text-[#a0b2af] max-w-xl mt-3">
          Recolhido em 1 clique a partir do email pós-limpeza. NPS = % promotores
          (9-10) menos % detratores (0-6). Acima de +50 é considerado excelente.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard label="NPS atual" value={totals.nps?.toFixed(1) ?? "—"}
                  accent={npsColor} testId="nps-stat-overall"
                  hint={`${totals.n || 0} respostas`} />
        <StatCard label="Média (0-10)"
                  value={(totals.avg || 0).toFixed(2)}
                  testId="nps-stat-avg" />
        <StatCard label="Promotores" value={totals.promoters || 0}
                  accent="#10b981" testId="nps-stat-promoters"
                  hint="Score 9-10" />
        <StatCard label="Passivos" value={totals.passives || 0}
                  accent="#f59e0b" testId="nps-stat-passives"
                  hint="Score 7-8" />
        <StatCard label="Detratores" value={totals.detractors || 0}
                  accent="#ef4444" testId="nps-stat-detractors"
                  hint="Score 0-6" />
      </div>

      {!hasData ? (
        <div className="lux-card p-12 text-center text-[#a0b2af] italic" data-testid="nps-empty">
          Sem respostas NPS nos últimos 6 meses. Os botões 0-10 do email pós-limpeza
          irão alimentar este painel automaticamente.
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="lux-card p-5" data-testid="nps-chart-volume">
            <div className="eyebrow mb-3">Distribuição mensal</div>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={series} stackOffset="expand"
                          margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a4640" />
                  <XAxis dataKey="month" tick={{ fill: "#a0b2af", fontSize: 10 }} />
                  <YAxis allowDecimals={false} tick={{ fill: "#a0b2af", fontSize: 10 }} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "#062c27" }} />
                  <Legend wrapperStyle={{ color: "#fdfbf7", fontSize: 12 }} />
                  <Bar stackId="a" dataKey="promoter"  name="Promotor"  fill="#10b981" />
                  <Bar stackId="a" dataKey="passive"   name="Passivo"   fill="#f59e0b" />
                  <Bar stackId="a" dataKey="detractor" name="Detrator"  fill="#ef4444" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="lux-card p-5" data-testid="nps-chart-trend">
            <div className="eyebrow mb-3">Evolução do NPS</div>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <LineChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a4640" />
                  <XAxis dataKey="month" tick={{ fill: "#a0b2af", fontSize: 10 }} />
                  <YAxis domain={[-100, 100]} tick={{ fill: "#a0b2af", fontSize: 10 }} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Line type="monotone" dataKey="nps" name="NPS"
                        stroke="#d4af37" strokeWidth={2}
                        dot={{ r: 4, fill: "#d4af37" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {recent.length > 0 && (
        <div className="lux-card overflow-hidden" data-testid="nps-recent">
          <div className="eyebrow p-5 pb-3">Últimas respostas</div>
          <table className="w-full text-sm">
            <thead className="bg-[#062c27] text-[10px] uppercase tracking-widest text-[#a0b2af]">
              <tr>
                <th className="text-left px-4 py-3">Data</th>
                <th className="text-left px-4 py-3">Alojamento</th>
                <th className="text-center px-3 py-3">Score</th>
                <th className="text-left px-4 py-3">Comentário</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r, i) => (
                <tr key={i} className="border-t border-[#1a4640]">
                  <td className="px-4 py-3 text-[#a0b2af]">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    {r.apartment_name || r.apt_type || "—"}
                  </td>
                  <td className="text-center px-3 py-3">
                    <span className="font-display text-lg" style={{
                      color: r.score >= 9 ? "#10b981"
                           : r.score >= 7 ? "#f59e0b" : "#ef4444",
                    }}>{r.score}</span>
                  </td>
                  <td className="px-4 py-3 text-[#a0b2af] italic">
                    {r.comment ? `«${r.comment}»` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
